import {ReactNode} from 'react'

import {ArchiveIcon} from '@/components/common/icons/ArchiveIcon'
import {BinocularsIcon} from '@/components/common/icons/BinocularsIcon'
import {CalendarPlusIcon} from '@/components/common/icons/CalendarPlusIcon'
import {DictionaryIcon} from '@/components/common/icons/DictionaryIcon'
import {FolderIcon} from '@/components/common/icons/FolderIcon'
import {GlobeIcon} from '@/components/common/icons/GlobeIcon'
import {GraduationCapIcon} from '@/components/common/icons/GraduationCapIcon'
import {ListIcon} from '@/components/common/icons/ListIcon'
import {MenuWithCursorIcon} from '@/components/common/icons/MenuWithCursorIcon'
import {PenInSquareIcon} from '@/components/common/icons/PenInSquareIcon'
import {PinIcon} from '@/components/common/icons/PinIcon'
import {ShieldPlusIcon} from '@/components/common/icons/ShieldPlusIcon'
import {ShieldWithStar} from '@/components/common/icons/ShieldWithStar'
import {TestTubeIcon} from '@/components/common/icons/TestTubeIcon'
import {TrademarkSymbolIcon} from '@/components/common/icons/TrademarkSymbolIcon'
import {UsersIcon} from '@/components/common/icons/UsersIcon'
import {UserWithCheckmarkIcon} from '@/components/common/icons/UserWithCheckmarkIcon'
import {LawFirmsServiceId} from '@/content/lawFirms'
import {theme} from '@/theme/theme'
import {Locale} from '@/utils/constants'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'

enum HeaderMenuItem {
  services = 'services',
  pricing = 'pricing',
  about = 'about',
  resources = 'resources',
}

export type SubmenuContentItem = {
  icon?: ReactNode
  title?: string
  description?: string
  linkHref: string
  trackedEvent?: string
  isHighlighted?: boolean
}

export type SubmenuProps = {
  closeNavigation?: () => void
}

type ServicesSubmenuType = Record<
  string,
  {
    title: string
    links: SubmenuContentItem[]
    numOfColumns: number
    button?: {linkHref: string; linkText: string}
    // Unindented links on desktop, vertically aligned with the multi-column list
    isAlignedWithList?: boolean
  }
>

const registrationSubmenu: ServicesSubmenuType[string] = {
  title: 'menu-tm-registration-title',
  links: [
    {
      icon: <TrademarkSymbolIcon />,
      title: 'menu-free-verification-title',
      description: 'menu-free-verification-desc',
      linkHref: routeTo.verification.form(),
      trackedEvent: 'ServicesSubmenu-FreeVerification',
      isHighlighted: true,
    },
    {
      icon: <MenuWithCursorIcon />,
      title: 'menu-how-it-works-title',
      linkHref: routeTo.howItWorks(),
      trackedEvent: 'ServicesSubmenu-HowItWorks',
    },
    {
      icon: <CalendarPlusIcon />,
      title: 'menu-tm-consultation-title',
      linkHref: routeTo.bookConsultation(),
      trackedEvent: 'ServicesSubmenu-BookConsultation',
    },
  ],
  numOfColumns: 1,
}

const servicesSubmenuEn: ServicesSubmenuType = {
  registration: registrationSubmenu,
  other: {
    title: 'menu-other-services-title',
    links: [
      {
        title: 'menu-tm-renewal-title',
        linkHref: routeTo.trademarkRenewal.home(),
        trackedEvent: 'ServicesSubmenu-TmRenewalCrossroad',
      },
      {
        title: 'menu-ownership-transfer-title',
        linkHref: routeTo.trademarkOwnershipTransfer.home(),
        trackedEvent: 'ServicesSubmenu-OwnershipTransfer',
      },
      {
        title: 'menu-brand-monitoring-title',
        linkHref: routeTo.brandMonitoring.home(),
        trackedEvent: 'ServicesSubmenu-BrandMonitoring',
      },
      {
        title: 'menu-statement-of-use-title',
        linkHref: routeTo.statementOfUse(),
        trackedEvent: 'ServicesSubmenu-StatementOfUse',
      },
      {
        title: 'menu-response-to-objections-title',
        linkHref: routeTo.oppositionResponse(),
        trackedEvent: 'ServicesSubmenu-ResponseToObjections',
      },
      {
        title: 'menu-declaration-of-use-title',
        linkHref: routeTo.declarationOfUse(),
        trackedEvent: 'ServicesSubmenu-DeclarationOfUse',
      },
      {
        title: 'menu-response-to-opposition-title',
        linkHref: routeTo.officeActionResponse(),
        trackedEvent: 'ServicesSubmenu-OfficeActionResponse',
      },
      {
        title: 'menu-c&d-letter-title',
        linkHref: routeTo.ceaseAndDesistLetter(),
        trackedEvent: 'ServicesSubmenu-C&Dletter',
      },
    ],
    numOfColumns: 2,
    button: {linkHref: routeTo.legalServices(), linkText: 'all-legal-services-title'},
  },
  lawFirms: {
    title: 'menu-for-law-firms-title',
    links: [
      {
        icon: <GlobeIcon color={theme.palette.text.primary} />,
        title: 'menu-global-ip-infrastructure-title',
        linkHref: routeTo.forLawFirms(LawFirmsServiceId.globalIpInfrastructure),
        trackedEvent: 'ServicesSubmenu-GlobalIpInfrastructure',
      },
      {
        icon: <BinocularsIcon color={theme.palette.text.primary} />,
        title: 'menu-tm-monitoring-title',
        linkHref: routeTo.forLawFirms(LawFirmsServiceId.trademarkMonitoring),
        trackedEvent: 'ServicesSubmenu-LawFirmsTmMonitoring',
      },
      {
        icon: <FolderIcon color={theme.palette.text.primary} />,
        title: 'menu-tm-management-title',
        linkHref: routeTo.forLawFirms(LawFirmsServiceId.trademarkManagement),
        trackedEvent: 'ServicesSubmenu-LawFirmsTmManagement',
      },
      {
        icon: <UserWithCheckmarkIcon color={theme.palette.text.primary} />,
        title: 'menu-schedule-intro-call-title',
        linkHref: routeTo.meetTrama(),
        trackedEvent: 'ServicesSubmenu-ScheduleIntroCall',
      },
    ],
    numOfColumns: 1,
    isAlignedWithList: true,
  },
}

const servicesSubmenu: ServicesSubmenuType = {
  registration: registrationSubmenu,
  brandProtection: {
    title: 'menu-brand-protection-title',
    links: [
      {
        icon: <ShieldWithStar color={theme.palette.text.primary} />,
        title: 'menu-brand-protection-lp-title',
        description: 'menu-brand-protection-lp-desc',
        linkHref: routeTo.brandMonitoring.home(),
        trackedEvent: 'ServicesSubmenu-BrandProtection',
        isHighlighted: true,
      },
      {
        icon: <ShieldPlusIcon color={theme.palette.text.primary} />,
        title: 'menu-report-threat-title',
        linkHref: routeTo.reportBrandInfringement(),
        trackedEvent: 'ServicesSubmenu-ReportThreat',
      },
      {
        icon: <CalendarPlusIcon />,
        title: 'menu-tm-consultation-title',
        linkHref: routeTo.bookConsultation(),
        trackedEvent: 'ServicesSubmenu-BookConsultation',
      },
    ],
    numOfColumns: 1,
  },
  other: {
    title: 'menu-other-services-title',
    links: [
      {
        title: 'menu-tm-renewal-title',
        linkHref: routeTo.trademarkRenewal.home(),
        trackedEvent: 'ServicesSubmenu-TmRenewalCrossroad',
      },
      {
        title: 'menu-ownership-transfer-title',
        linkHref: routeTo.trademarkOwnershipTransfer.home(),
        trackedEvent: 'ServicesSubmenu-OwnershipTransfer',
      },
      {
        title: 'menu-response-to-opposition-title',
        linkHref: routeTo.officeActionResponse(),
        trackedEvent: 'ServicesSubmenu-OfficeActionResponse',
      },
      {
        title: 'menu-response-to-objections-title',
        linkHref: routeTo.oppositionResponse(),
        trackedEvent: 'ServicesSubmenu-ResponseToObjections',
      },
      {
        title: 'menu-co-existence-agreement-title',
        linkHref: routeTo.coexistenceAgreement(),
        trackedEvent: 'ServicesSubmenu-Co-ExistenceAgreement',
      },
      {
        title: 'menu-c&d-letter-title',
        linkHref: routeTo.ceaseAndDesistLetter(),
        trackedEvent: 'ServicesSubmenu-C&Dletter',
      },
      {
        title: 'menu-statement-of-use-title',
        linkHref: routeTo.statementOfUse(),
        trackedEvent: 'ServicesSubmenu-StatementOfUse',
      },
      {
        title: 'menu-declaration-of-use-title',
        linkHref: routeTo.declarationOfUse(),
        trackedEvent: 'ServicesSubmenu-DeclarationOfUse',
      },
    ],
    numOfColumns: 2,
    button: {linkHref: routeTo.legalServices(), linkText: 'all-legal-services-title'},
  },
}

export enum ResourceSubmenuItem {
  howItWorks = 'how-it-works',
  blogs = 'blogs',
  storiesOfClients = 'stories-of-clients',
  trademarkDisputes = 'trademark-disputes',
  trademarkAcademy = 'trademark-academy',
  dictionary = 'dictionary',
  classAssist = 'class-assist',
  countryGuide = 'country-guide',
  specimenGuide = 'specimen-guide',
  brandProtectionGuide = 'brand-protection-guide',
  brandProtectionQAs = 'brand-protection-questions-and-answers',
}

export const resourcesSubmenuItemContent = {
  [ResourceSubmenuItem.howItWorks]: {
    icon: <MenuWithCursorIcon />,
    title: 'menu-how-it-works-title',
    description: 'menu-how-it-works-desc',
    linkHref: routeTo.howItWorks(),
    trackedEvent: 'ResourcesSubmenu-HowItWorks',
  },
  [ResourceSubmenuItem.blogs]: {
    icon: <PenInSquareIcon />,
    title: 'menu-resources-blogs-title',
    description: 'menu-resources-blogs-desc',
    linkHref: routeTo.blog.home(),
    trackedEvent: 'ResourcesSubmenu-BlogHome',
  },
  [ResourceSubmenuItem.storiesOfClients]: {
    icon: <UsersIcon color={theme.palette.text.primary} />,
    title: 'menu-resources-stories-of-clients-title',
    description: 'menu-resources-stories-of-clients-desc',
    linkHref: routeTo.blog.industryTopic('stories-of-our-clients'),
    trackedEvent: 'ResourcesSubmenu-StoriesOfOurClients',
  },
  [ResourceSubmenuItem.trademarkDisputes]: {
    icon: <ArchiveIcon />,
    title: 'menu-ip-case-database-title',
    description: 'menu-ip-case-database-desc',
    linkHref: routeTo.trademarkDisputes.home(),
    trackedEvent: 'ResourcesSubmenu-TrademarkDisputes',
  },
  [ResourceSubmenuItem.trademarkAcademy]: {
    icon: <GraduationCapIcon />,
    title: 'menu-resources-academy-title',
    description: 'menu-resources-academy-desc',
    linkHref: routeTo.tmAcademy.home(),
    trackedEvent: 'ResourcesSubmenu-TmAcademy',
  },
  [ResourceSubmenuItem.dictionary]: {
    icon: <DictionaryIcon />,
    title: 'menu-resources-dictionary-title',
    description: 'menu-resources-dictionary-desc',
    linkHref: routeTo.trademarkDictionary(),
    trackedEvent: 'ResourcesSubmenu-Dictionary',
  },
  [ResourceSubmenuItem.classAssist]: {
    icon: <ListIcon />,
    title: 'menu-resources-classes-title',
    description: 'menu-resources-classes-desc',
    linkHref: routeTo.classAssist.industry(),
    trackedEvent: 'ResourcesSubmenu-ClassAssist',
  },
  [ResourceSubmenuItem.countryGuide]: {
    icon: <PinIcon />,
    title: 'menu-resources-country-guide-title',
    description: 'menu-resources-country-guide-desc',
    linkHref: routeTo.countryGuide(),
    trackedEvent: 'ResourcesSubmenu-CountryGuide',
  },
  [ResourceSubmenuItem.specimenGuide]: {
    icon: <TestTubeIcon />,
    title: 'menu-resources-specimen-guide-title',
    description: 'menu-resources-specimen-guide-desc',
    linkHref: routeTo.specimenGuide.home(),
    trackedEvent: 'ResourcesSubmenu-SpecimenGuide',
  },
  [ResourceSubmenuItem.brandProtectionGuide]: {
    icon: <ArchiveIcon />,
    title: 'menu-resources-brand-protection-guide-title',
    description: 'menu-resources-brand-protection-guide-desc',
    linkHref: routeTo.brandProtectionGuide(),
    trackedEvent: 'ResourcesSubmenu-BrandProtectionGuide',
  },
  [ResourceSubmenuItem.brandProtectionQAs]: {
    icon: <DictionaryIcon />,
    title: 'menu-resources-brand-protection-qas-title',
    description: 'menu-resources-brand-protection-qas-desc',
    linkHref: routeTo.brandProtectionQA.home(),
    trackedEvent: 'ResourcesSubmenu-BrandProtectionQ&As',
  },
}

const useResourcesSubmenu = () => {
  const language = useLanguage()
  const isEnLanguage = language === Locale.en

  const defaultCommonItems = [
    ResourceSubmenuItem.howItWorks,
    ResourceSubmenuItem.blogs,
    ResourceSubmenuItem.storiesOfClients,
  ]

  const defaultItems = isEnLanguage
    ? [
        ...defaultCommonItems,
        ResourceSubmenuItem.trademarkDisputes,
        ResourceSubmenuItem.trademarkAcademy,
        ResourceSubmenuItem.classAssist,
        ResourceSubmenuItem.dictionary,
        ResourceSubmenuItem.countryGuide,
        ResourceSubmenuItem.specimenGuide,
      ]
    : [...defaultCommonItems, ResourceSubmenuItem.trademarkAcademy, ResourceSubmenuItem.classAssist]

  const brandProtectionItems = [
    ResourceSubmenuItem.trademarkAcademy,
    ResourceSubmenuItem.blogs,
    ResourceSubmenuItem.storiesOfClients,
    ...(isEnLanguage
      ? [ResourceSubmenuItem.brandProtectionGuide, ResourceSubmenuItem.brandProtectionQAs]
      : [ResourceSubmenuItem.howItWorks, ResourceSubmenuItem.classAssist]),
  ]

  return {defaultItems, brandProtectionItems}
}

const useServicesSubmenu = () => {
  const isEnLanguage = useLanguage() === Locale.en

  return {
    sections: isEnLanguage ? servicesSubmenuEn : servicesSubmenu,
    hasEvenSpacing: isEnLanguage,
  }
}

const useNavigation = () => ({
  [HeaderMenuItem.services]: {
    label: 'menu-services',
    submenu: useServicesSubmenu(),
    trackedEvent: 'Header-Services',
  },
  [HeaderMenuItem.pricing]: {
    label: 'menu-pricing',
    linkHref: routeTo.pricing(),
    trackedEvent: 'Header-Pricing',
  },
  [HeaderMenuItem.about]: {
    label: 'menu-about-trama',
    linkHref: routeTo.aboutUs.home(),
    trackedEvent: 'Header-AboutTrama',
  },
  [HeaderMenuItem.resources]: {
    label: 'menu-resources',
    submenu: useResourcesSubmenu(),
    trackedEvent: 'Header-Resources',
  },
})

export const useHeaderContent = () => ({
  getHelpButtonText: 'help-button',
  loginButtonText: 'menu-login',
  menu: {
    navigation: useNavigation(),
  },
  regionalSettings: {
    title: 'regional-settings-title',
    languageTitle: 'regional-settings-language-title',
    domainTitle: 'regional-settings-domain-title',
    currencyTitle: 'regional-settings-currency-title',
    saveButtonText: 'regional-settings-save',
  },
  resourcesIntro: {
    imageSrc: '/images/resources.svg',
    imageAlt: 'Resources',
    title: 'menu-resources',
    description: 'menu-resources-desc',
  },
})
